import React, { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import BeforeAfter from '../BeforeAfter/BeforeAfter'
import './WorkCarousel.css'

const WorkCarousel = ({
  title = 'Наши работы',
  items = [],
  isLoading = false,
  sectionClassName = '',
  footerDescription = '',
  controlsLabel = 'Состояние слайдера работ',
  prevAriaLabel = 'Предыдущие работы',
  nextAriaLabel = 'Следующие работы',
}) => {
  const getCardsPerView = () => {
    if (typeof window === 'undefined') {
      return 2
    }
    return window.innerWidth < 768 ? 1 : 2
  }

  const [cardsPerView, setCardsPerView] = useState(getCardsPerView)
  const [page, setPage] = useState(0)
  const totalPages = Math.max(Math.ceil(items.length / cardsPerView), 1)
  const maxPage = Math.max(totalPages - 1, 0)
  const safePage = Math.min(page, maxPage)
  const offset = safePage * cardsPerView
  const indicatorsCount = totalPages

  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setPage((prev) => Math.min(prev, maxPage))
  }, [maxPage])

  const handlePrev = () => {
    setPage((prev) => Math.max(prev - 1, 0))
  }

  const handleNext = () => {
    setPage((prev) => Math.min(prev + 1, maxPage))
  }

  if (!isLoading && items.length === 0) {
    return null
  }

  return (
    <section className={`work-carousel ${sectionClassName}`.trim()} aria-busy={isLoading}>
      <div className="work-carousel__inner">
        {title ? <h2 className="work-carousel__title">{title}</h2> : null}
        {footerDescription ? <p className="work-carousel__note">{footerDescription}</p> : null}

        <div className="work-carousel__viewport">
          <div
            className="work-carousel__track"
            style={{ '--work-offset': isLoading ? 0 : offset, '--cards-per-view': cardsPerView }}
          >
            {isLoading
              ? Array.from({ length: Math.max(cardsPerView, 2) }).map((_, index) => (
                  <div className="work-carousel__slide" key={`work-skeleton-${index}`} aria-hidden="true">
                    <div className="work-carousel__skeleton img-skeleton" />
                  </div>
                ))
              : items.map((item, index) => (
                  <div className="work-carousel__slide" key={item.id || `work-slide-${index}`}>
                    <BeforeAfter
                      beforeSrc={item.beforeSrc}
                      afterSrc={item.afterSrc}
                      altBefore={item.altBefore}
                      altAfter={item.altAfter}
                    />
                    {item.serviceTitle ? (
                      <div className="work-carousel__slide-title-wrap">
                        <p className="work-carousel__slide-title">{item.serviceTitle}</p>
                      </div>
                    ) : null}
                  </div>
                ))}
          </div>
        </div>

        {!isLoading && indicatorsCount > 1 && (
          <div className="work-carousel__controls">
            <div className="work-carousel__indicator" aria-label={controlsLabel}>
              {Array.from({ length: indicatorsCount }).map((_, index) => (
                <button
                  key={`work-dot-${index}`}
                  type="button"
                  className={`work-carousel__dot ${index === safePage ? 'is-active' : ''}`}
                  onClick={() => setPage(index)}
                  aria-label={`Перейти к набору работ ${index + 1}`}
                />
              ))}
            </div>

            <div className="work-carousel__arrows">
              <button
                type="button"
                className="work-carousel__arrow"
                onClick={handlePrev}
                disabled={safePage === 0}
                aria-label={prevAriaLabel}
              >
                <ChevronLeft size={18} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="work-carousel__arrow"
                onClick={handleNext}
                disabled={safePage === maxPage}
                aria-label={nextAriaLabel}
              >
                <ChevronRight size={18} strokeWidth={2.4} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default WorkCarousel
